import classNames from 'classnames';
import React from 'react';
import AccessibleDiv from 'components/AccessibleDiv/AccessibleDiv';
import ActionIcon from 'components/ActionIcon';
import { IconType } from 'components/Icon/constants';
import ResizeButton from 'components/ResizeButton';
import { ModalType, ProjectsConfig } from 'const';
import { CAROUSEL_ITEM_TYPES, CAROUSEL_STATIC_TYPES } from 'containers/Layouts/sagas/addCarousel';
import * as Models from 'models';
import { toJS } from 'utils/immutable';
import { isGroupLayout } from 'utils/layouts/isGroupLayout';
import { ActionsColor, LAYOUT_NAME_HINT_MAX_LENGTH } from './constants';
import { ArtboardLayoutActionsProps, IArtboardLayoutActionsState } from './models';
import styles from './styles.module.scss';
import { getLayoutActionsConfig } from './utils';

export default class ArtboardLayoutActions extends React.Component<ArtboardLayoutActionsProps, IArtboardLayoutActionsState> {
  readonly state: IArtboardLayoutActionsState = {
    hideActionsAfterResize: false,
    rightActionsExpanded: false,
  };

  private readonly actions = React.createRef<HTMLDivElement>();

  componentDidUpdate(prevProps: ArtboardLayoutActionsProps): void {
    const layoutId = (this.props.layout as Models.LayoutMap).get('id');
    const prevIsActionFrozen = prevProps.frozenEntityId === layoutId;
    const currentIsActionFrozen = this.props.frozenEntityId === layoutId;

    if (prevIsActionFrozen === currentIsActionFrozen) {
      return;
    }
    if (currentIsActionFrozen) {
      this.showActions();
    } else {
      this.hideActions();
    }
  }

  private readonly deleteLayout = (): void => {
    const { layout, deleteLayout } = this.props;
    const layoutId = layout.get('id');
    deleteLayout(layoutId);
  };

  private readonly addLayoutColumn = (): void => {
    this.props.addLayoutColumn();
  };

  private readonly addLayoutRow = (): void => {
    this.props.addLayoutRow();
  };

  private readonly saveAsReusableLayout = (): void => {
    const { layout, sectionDisplayName = '', showModal, setLayout } = this.props;
    setLayout(layout);
    showModal(ModalType.SAVE_LAYOUT, { layout: toJS(layout as Models.LayoutMap), sectionDisplayName });
  };

  private readonly copyLayout = (): void => {
    const { layout, copyLayout, setLayout } = this.props;
    setLayout(layout);
    copyLayout(layout.get('id'));
  };

  private readonly unlinkReusableLayout = (): void => {
    const { unlinkReusableLayout, layout } = this.props;

    unlinkReusableLayout(layout.get('id'));
  };

  private readonly setMoveableLayout = (isDraggable: boolean): void => {
    const { layout, setMoveableLayout } = this.props;
    const layoutId = layout.get('id');
    setMoveableLayout(isDraggable ? layoutId : '');
  };

  private readonly allowDragging = (): void => {
    const { disableDragAndDelete } = this.props;
    this.showActions();

    if (!disableDragAndDelete) {
      this.setMoveableLayout(true);
    }
  };

  private readonly disableDragging = (): void => {
    const { disableDragAndDelete } = this.props;
    this.hideActions();

    if (!disableDragAndDelete) {
      this.setMoveableLayout(false);
    }
  };

  private readonly hideActions = (): void => {
    const layoutId = (this.props.layout as Models.LayoutMap).get('id');
    if (this.props.isResizingLayout) {
      this.setState({
        hideActionsAfterResize: true,
      });

      return;
    }

    this.setState({
      rightActionsExpanded: false,
    });

    if (this.props.frozenEntityId === layoutId) {
      return;
    }
    this.props.removeActiveLayoutId(layoutId);
  };

  private readonly showActions = (): void => {
    if (this.props.isResizingLayout) {
      this.setState({
        hideActionsAfterResize: false,
      });

      return;
    }
    if (!this.props.areActionsDisabled) {
      this.props.addActiveLayoutId(this.props.layout.get('id'));
    }
  };

  private readonly expandRightActions = (): void => {
    this.setState({ rightActionsExpanded: true });
  };

  private readonly handleMouseEnterOnRightActionMenu = (): void => {
    this.expandRightActions();
    this.showActions();
  };

  private readonly onResizeEnd = (): void => {
    if (this.state.hideActionsAfterResize) {
      this.hideActions();
    }
    this.props.handleDragStop();
  };

  private readonly onSetFrozenAction = (event: React.MouseEvent<HTMLDivElement>): void => {
    event.stopPropagation();
    const layoutId = (this.props.layout as Models.LayoutMap).get('id');
    if (this.props.frozenEntityId === layoutId) {
      this.props.setFrozenEntityIdAction(null);

      return;
    }
    this.props.setFrozenEntityIdAction(layoutId);
  };

  private readonly renderBorder = (backgroundColor: ActionsColor): JSX.Element => (
    <AccessibleDiv
      className={classNames(styles.border, styles[`backgroundColor_${backgroundColor}`])}
      onMouseEnter={this.showActions}
      onMouseLeave={this.hideActions}
      onClick={this.onSetFrozenAction}
    />
  );

  render(): React.ReactNode {
    const { rightActionsExpanded } = this.state;
    const {
      activeLayoutIds,
      addingDisabled,
      connectDragSource,
      disableDragAndDelete,
      documents,
      editMode,
      enableLayoutEditMode,
      handleDrag,
      handleDragStop,
      height,
      hideButtons,
      hint,
      isBeingUploaded,
      isChildLayout,
      isResizeAvailable,
      layout,
      projectType,
      showActions,
      isReadOnly,
      dataTestId = 'section0-layout0',
      frozenEntityId,
    } = this.props;
    const layoutId = (layout as Models.LayoutMap).get('id');
    const isActionFrozen = frozenEntityId === layoutId;
    const carouselItem = (layout as Models.LayoutMap).get('carouselItem');
    const carouselItemType = carouselItem?.get('type');
    if (carouselItemType === CAROUSEL_ITEM_TYPES.CONTROLS) {
      return null;
    }
    const isDisabledCopyByCarouselItemType = carouselItemType && CAROUSEL_STATIC_TYPES.includes(carouselItemType);
    const {
      addColumnAvailable,
      addRowAvailable,
      backgroundColor,
      borderColor,
      copyLayoutAvailable,
      editModeAvailable,
      resizeBtnControlledByEditMode,
      rightActionsSize,
      saveAsReusableAvailable,
      unlinkAvailable,
    } = getLayoutActionsConfig(layout, documents, isChildLayout);
    const groupLayout = isGroupLayout(layout);
    const { allowScrollableLayout } = ProjectsConfig[projectType];
    const layoutName = hint && (hint.length < LAYOUT_NAME_HINT_MAX_LENGTH ? hint : `${hint.substring(0, LAYOUT_NAME_HINT_MAX_LENGTH)}...`);
    const showEditButton = editModeAvailable && !isReadOnly;
    const showAddButton = addColumnAvailable && !isReadOnly;
    const showCopyButton = copyLayoutAvailable && !isReadOnly && !isDisabledCopyByCarouselItemType;
    const showUnlinkButton = unlinkAvailable && !isReadOnly;
    const showSaveButton = saveAsReusableAvailable && !isReadOnly;
    const showResizeButton = !isReadOnly;
    const showRightActions = !isReadOnly || !isChildLayout;

    const actionsDataTestId = `${dataTestId}-actions`;
    const actionsRightDataTestId = `${dataTestId}-actionsRight`;
    const actionsLeftDataTestId = `${dataTestId}-actionsLeft`;
    const actionsBottomDataTestId = `${dataTestId}-actionsBottom`;

    const actionsVisible =
      editMode ||
      showActions ||
      activeLayoutIds.has(layoutId) ||
      (groupLayout && (layout as Models.GroupLayoutMap).get('layoutIds').some(id => activeLayoutIds.has(id)));

    const actionsClassNames = classNames(
      styles.ArtboardLayoutActions,
      styles[`borderColor_${borderColor}`],
      {
        [styles.hideButtons]: hideButtons,
        [styles.showActions]: actionsVisible,
        [styles.shiftActions]: groupLayout, // temporary solution, will be revised from UX perspective
        [styles.frozen]: isActionFrozen,
      },
    );

    const renderResizeButton = (actionsTestId: string = actionsDataTestId): React.ReactElement | null | boolean => {
      if (!allowScrollableLayout || !isResizeAvailable) {
        return null;
      }

      const resizeBtnDataTestId = `${actionsTestId}-resizeBtn`;

      if (resizeBtnControlledByEditMode) {
        return editMode && (
          <ResizeButton
            handleDrag={handleDrag}
            handleDragStop={handleDragStop}
            height={height}
            disabled={isBeingUploaded}
            dataTestId={resizeBtnDataTestId}
          />
        );
      }

      return (
        <ResizeButton
          onMouseEnter={this.showActions}
          onMouseLeave={this.hideActions}
          handleDragStop={this.onResizeEnd}
          handleDrag={handleDrag}
          color={borderColor}
          height={height}
          disabled={isBeingUploaded}
          shiftResizeButton={true}
          dataTestId={resizeBtnDataTestId}
        />
      );
    };

    const renderAddRowButton = (actionsTestId: string = actionsDataTestId): JSX.Element | null => {
      if (!addRowAvailable) {
        return null;
      }
      const dataTestId = `${actionsTestId}-actionIcon${IconType.ADD}`;

      return (
        <ActionIcon
          backgroundColor={backgroundColor}
          color={isBeingUploaded ? 'light-grey' : 'secondary'}
          isDisabled={isBeingUploaded}
          onClick={this.addLayoutRow}
          onMouseEnter={this.showActions}
          onMouseLeave={this.hideActions}
          size="xs"
          type={IconType.ADD}
          dataTestId={dataTestId}
        />
      );
    };

    return (
      <div
        className={actionsClassNames}
        ref={this.actions}
        data-testid={actionsDataTestId}
      >
        {hint && <div className={styles.hint}>{layoutName}</div>}
        <div className={styles.left} data-testid={actionsLeftDataTestId}>
          {this.renderBorder(backgroundColor)}
          {
            showResizeButton && connectDragSource(
              <div
                className={classNames(styles.actions)}
                onMouseEnter={this.allowDragging}
                onMouseLeave={this.disableDragging}
                onMouseDown={this.hideActions}
                onMouseUp={this.showActions}
              >
                <ActionIcon
                  backgroundColor={backgroundColor}
                  color="secondary"
                  isDisabled={isChildLayout && isBeingUploaded || disableDragAndDelete}
                  size="badge"
                  type={IconType.UP_AND_DOWN_ARROWS}
                  dataTestId={`${actionsLeftDataTestId}-actionIcon${IconType.UP_AND_DOWN_ARROWS}`}
                />
              </div>,
            )
          }
        </div>
        <div className={styles.right} data-testid={actionsRightDataTestId}>
          {this.renderBorder(backgroundColor)}
          {
            showRightActions && <AccessibleDiv
              className={classNames(
                styles.actions,
                styles[`rightActionsSize_${rightActionsSize}`],
                { [styles.actions__expanded]: rightActionsExpanded },
              )}
              onMouseEnter={this.handleMouseEnterOnRightActionMenu}
              onMouseLeave={this.hideActions}
            >
              {
                rightActionsExpanded
                  ?
                  <>
                    { showAddButton && <ActionIcon
                      backgroundColor={backgroundColor}
                      color={isBeingUploaded || addingDisabled ? 'light-grey' : 'secondary'}
                      isDisabled={isBeingUploaded || addingDisabled}
                      onClick={this.addLayoutColumn}
                      size="xs"
                      type={IconType.ADD}
                      dataTestId={`${actionsRightDataTestId}-actionIcon${IconType.ADD}`}
                    />}
                    { showEditButton && <ActionIcon
                      backgroundColor={backgroundColor}
                      color="secondary"
                      isDisabled={isBeingUploaded || !enableLayoutEditMode}
                      onClick={enableLayoutEditMode}
                      size="xs"
                      type={IconType.GEAR}
                      dataTestId={`${actionsRightDataTestId}-actionIcon${IconType.GEAR}`}
                    />}
                    { showCopyButton && <ActionIcon
                      backgroundColor={backgroundColor}
                      color={'secondary'}
                      onClick={this.copyLayout}
                      size="xs"
                      type={IconType.COPY}
                      dataTestId={`${actionsRightDataTestId}-actionIcon${IconType.COPY}`}
                    />}
                    { showUnlinkButton && <ActionIcon
                      backgroundColor={backgroundColor}
                      color={isBeingUploaded ? 'light-grey' : 'secondary'}
                      isDisabled={isBeingUploaded}
                      onClick={this.unlinkReusableLayout}
                      size="xs"
                      type={IconType.UNLINK}
                      dataTestId={`${actionsRightDataTestId}-actionIcon${IconType.UNLINK}`}
                    />}
                    { showSaveButton && <ActionIcon
                      backgroundColor={backgroundColor}
                      color={'secondary'}
                      onClick={this.saveAsReusableLayout}
                      size="xs"
                      type={IconType.SAVE_LAYOUT}
                      dataTestId={`${actionsRightDataTestId}-actionIcon${IconType.SAVE_LAYOUT}`}
                    />}
                    <ActionIcon
                      backgroundColor={backgroundColor}
                      color="secondary"
                      isDisabled={isBeingUploaded || disableDragAndDelete}
                      onClick={this.deleteLayout}
                      size="xs"
                      type={IconType.DELETE}
                      dataTestId={`${actionsRightDataTestId}-actionIcon${IconType.DELETE}`}
                    />
                  </>
                  :
                  <ActionIcon
                    backgroundColor={backgroundColor}
                    color="secondary"
                    size="xs"
                    type={IconType.MENU}
                    dataTestId={`${actionsRightDataTestId}-actionIcon${IconType.MENU}`}
                  />
              }
            </AccessibleDiv>
          }
        </div>
        <div className={styles.bottom} data-testid={actionsBottomDataTestId}>
          {!isReadOnly && renderAddRowButton(actionsBottomDataTestId)}
          {renderResizeButton(actionsBottomDataTestId)}
        </div>
      </div>
    );
  }
}
